import { useState, useCallback } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  isConnectWalletDialogOpenVar,
  isConnectWalletOpenVar
} from 'features/auth/store/dialogs'
import { isWalletsDialogOpenVar } from 'features/wallets/store/dialogs'
import { WalletStateStatus } from 'shared/constants'
import { usePlayWallet } from 'shared/hooks/use-play-wallet'
import { PlayWalletStatus } from 'shared/store/play-wallet'
import { userVar } from 'shared/store/user'
import { wizardModeVar } from '../../features/auth/store/wizard.store'
import { WizardMode } from '../../features/auth/types'
import {
  WalletStateContextProvider,
  type WalletState
} from './wallet-state-context'

const { signInToPlay, connectWallet, linkWallet, switchWallet } =
  WalletStateStatus

const TITLE_BY_STATUS: Record<WalletStateStatus, string> = {
  [WalletStateStatus.signInToPlay]: 'Sign in to Play',
  [WalletStateStatus.connectWallet]: 'Connect wallet',
  [WalletStateStatus.switchChain]: 'Switch chain',
  [WalletStateStatus.linkWallet]: 'Link wallet',
  [WalletStateStatus.switchWallet]: 'Switch wallet'
}

function getStatusKey(
  user: boolean,
  isConnected: boolean,
  playWalletStatus: PlayWalletStatus | null
): WalletStateStatus | null {
  if (!user) return signInToPlay
  if (!isConnected) return connectWallet
  if (playWalletStatus === PlayWalletStatus.notLinked) return linkWallet
  if (playWalletStatus === PlayWalletStatus.notSelected) return switchWallet
  return null
}

export function SolanaWalletStateProvider({
  children
}: {
  children: React.ReactNode
}) {
  const user = useReactiveVar(userVar)
  const [isLoading, setIsLoading] = useState(false)
  const { connected: isConnected, publicKey } = useWallet()
  const { status: playWalletStatus } = usePlayWallet()

  const walletStateKey = getStatusKey(
    Boolean(user),
    isConnected && Boolean(publicKey),
    playWalletStatus
  )
  const isChainIdValid = true

  const handlePrepareWallet = useCallback(async () => {
    setIsLoading(true)

    if (!user) {
      isConnectWalletDialogOpenVar(true)
      wizardModeVar(WizardMode.connectWallet)
    }

    if (!isConnected) {
      isConnectWalletOpenVar(true)
    }

    if (user && isConnected) {
      // NOTE: the connected wallet is not linked or not selected for play
      isWalletsDialogOpenVar(true)
    }

    setIsLoading(false)
  }, [user, isConnected])

  const value: WalletState = {
    isReady: walletStateKey === null,
    isChainIdValid,
    isConnected,
    title: walletStateKey ? TITLE_BY_STATUS[walletStateKey] : null,
    handlePrepareWallet,
    loading: isLoading,
    walletStateKey
  }

  return (
    <WalletStateContextProvider value={value}>
      {children}
    </WalletStateContextProvider>
  )
}
